import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import _ from '@lodash';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import states from 'app/shared-components/controls/states.json';
import ControlledTextField from 'app/shared-components/form/ControlledTextField';
import ControlledSingleSelect from 'app/shared-components/form/ControlledSingleSelect';
import { selectLocationById, createLocation, updateLocation } from 'app/store/orgLocationsSlice';
import dispatchSubmit from 'app/shared-components/util/dispatchSubmit';
import LocationModel from '../model/LocationModel';
import EntityForm from 'app/shared-components/layout/EntityForm';

const defaultValues= LocationModel();

/**
 * Form Validation Schema
 */
const schema = yup.object().shape({
  name: yup.string().required('You must enter a name'),
  phone: yup.string(),
  address: yup.string(),
  city: yup.string(),
  stateProvince: yup.string(),
  postalCode: yup.string(),
  email: yup.string().email('You must enter a valid email'),
  website: yup.string(),
});

function LocationForm(props) {
  const routeParams = useParams();
  const {groupId, locationId} = routeParams;
  const isNew = locationId === 'new';
  const location = isNew ? LocationModel() : LocationModel(useSelector(selectLocationById(locationId)));
  const dispatch = useDispatch();
  const title= isNew ? "New Location" : "Edit " + location?.name;
  const returnPath= "/apps/organization/" + groupId + "/locations";

  const { control, watch, reset, handleSubmit, formState, getValues } = useForm({
	defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const { isValid, dirtyFields, errors } = formState;

  useEffect(() => {
    reset({ ...location });
  }, [ locationId, reset]);

  /**
   * Form Submit
   */
  function onSubmit(data) {
	const loc= { ...data, id: locationId, organizationId: groupId, unread: undefined, programs: undefined};
	dispatchSubmit({dispatch: dispatch, 
	                submitFunction: updateLocation(loc), 
	                returnPath: returnPath, 
	                successMessage:  "Location Updated!"});
  }

  function onSubmitNew(data) {
    const loc= { ...data, organizationId: groupId};
	dispatchSubmit({dispatch: dispatch, 
	                submitFunction: createLocation(loc), 
	                returnPath: returnPath, 
	                successMessage:  "Location Created!"});	
  }

  return (
	<EntityForm title={title} 
	            submitTitle={isNew ? "Create" : "Update"} 
	            onSubmit={isNew ? onSubmitNew : onSubmit}
	            handleSubmit={handleSubmit} isValid={isValid} dirtyFields={dirtyFields}
	            returnPath={returnPath}
	>
        <ControlledTextField name="name" label="Location Name"
                             control={control} errors={errors}
             				 autoFocus required fullWidth />
    
        <ControlledTextField name="address" label="Street Address"
                             control={control} errors={errors}
             				 fullWidth />
        <ControlledTextField name="city" label="City"
                             control={control} errors={errors}
             				 fullWidth />     				 
        <ControlledSingleSelect name="stateProvince" label="State" className="mt-0 mb-0"
                                control={control} errors={errors}
                                items={states} />     				 
             				      				 
        <ControlledTextField name="postalCode" label="Postal Code"
                             control={control} errors={errors}
             				 fullWidth />      				 

        <ControlledTextField name="phone" label="Phone Number"
                             control={control} errors={errors}
             				 fullWidth />  
        
        <ControlledTextField name="email" label="Email Address"
                             control={control} errors={errors}
             				 fullWidth />
             				 
        <ControlledTextField name="website" label="Website URL"
                             control={control} errors={errors}
             				 fullWidth />
        
     				 
    </EntityForm>
      
  );
}

export default LocationForm;
